'use client';

import { useSession } from '@shared/auth';
import { getSiteConfig } from '@shared/config';
import { useDataLayerTrackEvent } from '@shared/tracking';
import { ROUTES, useClientConfig } from '@shared/utils';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCheckoutSubmittedTicket } from '../../../checkout/hooks/useCheckoutSubmittedTicket';
import type { CheckoutPurchaseConfirmation } from '../../../checkout/types';
import { AccountSuspensionGate } from '../../../my-account/AccountSuspensionGate/AccountSuspensionGate';
import { isAccountSuspendedStatus } from '../../../my-account/utils/isAccountSuspendedStatus';
import { DEPOSIT_PAYMENT_METHODS } from '../../../PaymentMethodSelector/PaymentMethods';
import type { MethodOrInstrument } from '../../../PaymentMethodSelector/types';
import { useGlobalSheet } from '../../context/GlobalSheetContext';
import type { DepositSuccessData } from '../DepositSheet/types';
import { useCallSubmitCart } from './hooks/useCallSubmitCart';
import { useCheckoutFlow } from './hooks/useCheckoutFlow';
import { useCheckoutPayment } from './hooks/useCheckoutPayment';
import { mapCartSubmitError } from './mapCartSubmitError';
import { CheckoutSummaryStep } from './steps/CheckoutSummaryStep/CheckoutSummaryStep';
import { ErrorStep } from './steps/ErrorStep/ErrorStep';
import { getCheckoutScenario, getTicketSubmissionCartItem, type CartResponse, type CheckoutCartSummary, type CheckoutFlowOrchestratorProps, type CheckoutState } from './types';
import {
  CHECKOUT_DIALOG_TRACKING,
  buildCheckoutPaymentFailureTracking,
  getDepositTrackingType,
  mapCheckoutCartSubmitErrorContainerTracking,
  mapCheckoutCartSubmitErrorTracking,
  mapCheckoutPaymentErrorTracking,
} from './utils/checkoutDialogTracking';

/**
 * Returns true for methods that don't require a separate details step in the
 * Deposit Sheet — i.e. payment methods other than Debit card (which needs
 * card detail entry) and Apple Pay (which triggers a native browser dialog).
 * These methods are processed inline within the Checkout Sheet.
 */
const methodHasNoDetailsStep = (method: MethodOrInstrument | null): boolean => {
  if (!method) return false;
  return method.name !== DEPOSIT_PAYMENT_METHODS.CARD.name && method.name !== DEPOSIT_PAYMENT_METHODS.APPLE_PAY.name;
};

function buildCheckoutPurchaseConfirmation(
  cartResponse: CartResponse,
  cartSummary: CheckoutCartSummary,
  currency: string,
  productDisplayName?: string
): CheckoutPurchaseConfirmation {
  const ticket = cartResponse.tickets?.[0];
  const confirmation: CheckoutPurchaseConfirmation = {
    orderDetails: {
      orderId: String(cartResponse.orderId),
    },
    products: {},
    pricing: {
      itemCount: cartResponse.tickets?.length ?? 0,
      subtotal: cartSummary.stake,
      usedBalance: cartSummary.usedBalance,
      bonusDiscount: cartSummary.bonusDiscount ?? 0,
      total: cartSummary.stake - (cartSummary.bonusDiscount ?? 0),
      currency,
    },
  };

  if (ticket?.type === 'LOTTO') {
    return {
      ...confirmation,
      products: {
        lottery: {
          type: 'lottery',
          lotteryId: ticket.lotteryId,
          ticketId: ticket.ticketId,
          lines: ticket.lines.length,
          draws: ticket.lifespan.participations.length,
        },
      },
    };
  }

  if (ticket?.type === 'SCRATCHCARD') {
    return {
      ...confirmation,
      products: {
        scratchcard: {
          type: 'scratchcard',
          name: productDisplayName ?? ticket.name,
          quantity: ticket.quantity,
          title: ticket.name,
        },
      },
    };
  }

  return confirmation;
}

function CheckoutFlowOrchestrator({ cartSummary, ticketDetails, onSuccess, onStepChange, onStateChange, ticketMeta, productDisplayName }: CheckoutFlowOrchestratorProps) {
  const { suspendedNotificationNamespaces } = getSiteConfig().MY_ACCOUNT_SETTINGS;
  const router = useRouter();
  const { session, update } = useSession();
  const { currency } = useClientConfig();
  const { setCheckoutSubmittedTicket } = useCheckoutSubmittedTicket();
  const { openDepositSheet, closeDepositSheet } = useGlobalSheet();
  const playerId = session.profile?.id ?? '';
  const { submitCart } = useCheckoutFlow({ playerId });
  const dataLayerTrackEvent = useDataLayerTrackEvent();
  const selectedPaymentMethodRef = useRef<string>('');
  const selectedMethodRef = useRef<MethodOrInstrument | null>(null);
  const [state, setState] = useState<CheckoutState>({ kind: 'ready', scenario: getCheckoutScenario(cartSummary, ticketMeta) });
  const [isRedirecting, setIsRedirecting] = useState(false);
  const defaultAmount = (cartSummary.payable / 100).toString();
  const [payAmount, setPayAmount] = useState<string>(defaultAmount);
  const [resolvedDepositTrackingType, setResolvedDepositTrackingType] = useState<'FTD' | 'SD' | undefined>();

  const persistDepositTrackingType = useCallback((depositData?: DepositSuccessData) => {
    if (depositData && typeof depositData.isFirstPayIn === 'boolean') {
      setResolvedDepositTrackingType(getDepositTrackingType(depositData.isFirstPayIn));
    }
  }, []);

  const trackTicketSubmission = useCallback(
    (cartResponse: CartResponse, depositData?: DepositSuccessData) => {
      dataLayerTrackEvent({
        event: 'ticketSubmission',
        submission: {
          ticketSize: cartResponse.tickets?.length ?? 0,
          orderId: cartResponse.orderId,
          cartPrice: cartResponse.stake / 100,
          payInAmount: parseFloat(payAmount) || 0,
          paymentMethod: selectedPaymentMethodRef.current,
          isFirstPayIn: depositData?.isFirstPayIn ?? false,
          daysSinceFtd: depositData?.daysSinceFtd ?? 0,
          bonusCode: cartResponse.bonusDiscount,
        },
        cart: {
          currency,
          items: (cartResponse.tickets ?? []).map(getTicketSubmissionCartItem),
          totalPriceGross: (cartResponse.tickets ?? []).reduce((sum, ticket) => sum + ticket.price, 0) / 100,
          totalPlayedLines: (cartResponse.tickets ?? []).reduce((sum, ticket) => {
            return sum + getTicketSubmissionCartItem(ticket).playedLines;
          }, 0),
        },
      });
    },
    [currency, dataLayerTrackEvent, payAmount]
  );

  const redirectToThankYouPage = useCallback(
    (cartResponse: CartResponse) => {
      const submittedTicket = buildCheckoutPurchaseConfirmation(cartResponse, cartSummary, currency, productDisplayName);
      setCheckoutSubmittedTicket(submittedTicket);
      router.push(ROUTES.CHECKOUT.THANK_YOU);
      setIsRedirecting(true);
    },
    [cartSummary, currency, productDisplayName, router, setCheckoutSubmittedTicket]
  );
  const completePurchase = useCallSubmitCart({
    submitCart,
    currency,
    ticketMeta,
    onSuccess,
    onOrderSubmitted: trackTicketSubmission,
    redirectToThankYouPage,
  });

  const setSubmitCartErrorState = useCallback((error: unknown) => {
    const { scenario, titleKey, messageKey, retryTarget, secondaryAction } = mapCartSubmitError(error);
    const dialogTracking = mapCheckoutCartSubmitErrorTracking(scenario);
    const errorTrackContainer = mapCheckoutCartSubmitErrorContainerTracking(scenario);

    setState({
      kind: 'error',
      titleKey,
      messageKey,
      retryTarget,
      ...(secondaryAction ? { secondaryAction } : {}),
      ...(dialogTracking ? { dialogTracking } : {}),
      ...(errorTrackContainer ? { errorTrackContainer } : {}),
    });
  }, []);

  const handleSubmitCart = useCallback(
    async (depositData?: DepositSuccessData) => {
      persistDepositTrackingType(depositData);
      setState({ kind: 'submitting', scenario: getCheckoutScenario(cartSummary, ticketMeta) });
      try {
        const refreshedSession = await update();
        if (isAccountSuspendedStatus(refreshedSession.profile?.status)) {
          setState({ kind: 'ready', scenario: getCheckoutScenario(cartSummary, ticketMeta) });
          return;
        }

        await completePurchase(depositData);
      } catch (error) {
        setSubmitCartErrorState(error);
      }
    },
    [cartSummary, completePurchase, persistDepositTrackingType, setSubmitCartErrorState, ticketMeta, update]
  );

  const { isProcessing, processPayment, resetPayment } = useCheckoutPayment({
    amount: payAmount,
    defaultDepositType: resolvedDepositTrackingType,
    onSuccess: handleSubmitCart,
    onError: ({ message, depositType, context }) => {
      if (depositType) {
        setResolvedDepositTrackingType(depositType);
      }

      setState({
        kind: 'error',
        message,
        ...(context?.errorKey ? { errorKey: context.errorKey } : {}),
        retryTarget: 'payment',
        paymentFailureTracking: buildCheckoutPaymentFailureTracking(selectedMethodRef.current, payAmount, depositType),
        dialogTracking: mapCheckoutPaymentErrorTracking(context),
      });
    },
  });

  const handleRetryPayment = useCallback(() => {
    resetPayment();
    setState({ kind: 'ready', scenario: getCheckoutScenario(cartSummary, ticketMeta) });
  }, [resetPayment, cartSummary, ticketMeta]);

  const handleRetryToSummary = useCallback(() => {
    resetPayment();
    setState({ kind: 'ready', scenario: getCheckoutScenario(cartSummary, ticketMeta) });
  }, [resetPayment, cartSummary, ticketMeta]);

  const openHelpCentre = useCallback(() => {
    window.open(ROUTES.HELP_CENTRE.BASE_URL, '_blank', 'noopener,noreferrer');
  }, []);

  const isBusy = state.kind === 'submitting' || isProcessing;

  useEffect(() => {
    onStepChange?.(state.kind !== 'error');
  }, [state.kind, onStepChange]);

  useEffect(() => {
    onStateChange?.({ isSubmitting: isBusy, isRedirecting });
  }, [isBusy, isRedirecting, onStateChange]);

  const handleOpenDepositLimits = useCallback(() => {
    openDepositSheet({ openOnDepositLimits: true });
  }, [openDepositSheet]);

  const handlePay = useCallback(
    async (selectedMethod: MethodOrInstrument | null) => {
      if (state.kind !== 'ready') {
        return;
      }

      selectedPaymentMethodRef.current = selectedMethod?.apiName ?? '';
      selectedMethodRef.current = selectedMethod;

      if (cartSummary.payable === 0) {
        await handleSubmitCart();
        return;
      }

      if (methodHasNoDetailsStep(selectedMethod)) {
        resetPayment();
        await processPayment(selectedMethod!);
        return;
      }

      openDepositSheet({
        initialAmount: payAmount,
        autoProcessPaymentMethod: selectedMethod ?? undefined,
        skipSuccessStep: true,
        onSuccessCallback: async (depositData?: DepositSuccessData) => {
          persistDepositTrackingType(depositData);
          setState({ kind: 'submitting', scenario: getCheckoutScenario(cartSummary, ticketMeta) });
          try {
            await completePurchase(depositData);
            closeDepositSheet();
          } catch (error) {
            setSubmitCartErrorState(error);
          }
        },
      });
    },
    [
      state.kind,
      cartSummary,
      ticketMeta,
      openDepositSheet,
      payAmount,
      handleSubmitCart,
      resetPayment,
      processPayment,
      completePurchase,
      closeDepositSheet,
      persistDepositTrackingType,
      setSubmitCartErrorState,
    ]
  );

  const retryHandlers = {
    'submit-cart': handleSubmitCart,
    payment: handleRetryPayment,
    summary: handleRetryToSummary,
    none: undefined,
  } as const;

  return (
    <AccountSuspensionGate
      namespace={suspendedNotificationNamespaces.payments}
      variant="floating"
      className="p-5"
      dataTrackType={CHECKOUT_DIALOG_TRACKING.accountOnHoldVerifyIdentity.dataTrackType}
      dataTrackMessage={CHECKOUT_DIALOG_TRACKING.accountOnHoldVerifyIdentity.dataTrackMessage}
    >
      <div data-testid="checkout-flow-orchestrator">
        {state.kind === 'error' ? (
          <ErrorStep
            errorMessage={state.message}
            titleKey={state.titleKey}
            messageKey={state.messageKey}
            errorKey={state.errorKey}
            onRetry={retryHandlers[state.retryTarget]}
            showRetry={state.retryTarget !== 'none'}
            secondaryAction={state.secondaryAction === 'contact-support' ? { labelKey: 'bet_limit.action', onClick: openHelpCentre } : undefined}
            dialogTracking={state.dialogTracking}
            paymentFailureTracking={state.paymentFailureTracking}
            errorTrackContainer={state.errorTrackContainer}
          />
        ) : (
          <CheckoutSummaryStep
            cartSummary={cartSummary}
            ticketDetails={ticketDetails}
            isSubmitting={isBusy}
            scenario={state.scenario}
            depositAmount={payAmount}
            depositTrackingType={resolvedDepositTrackingType}
            onDepositAmountChange={setPayAmount}
            onPay={method => void handlePay(method)}
            onOpenDepositLimits={handleOpenDepositLimits}
          />
        )}
      </div>
    </AccountSuspensionGate>
  );
}

export { CheckoutFlowOrchestrator };
